import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";

type FormulationPromptPopoverProps = {
  anchorRect: DOMRect | null;
  onSubmit: (text: string) => void;
  onClose: () => void;
};

export function FormulationPromptPopover({
  anchorRect,
  onSubmit,
  onClose,
}: FormulationPromptPopoverProps) {
  const [text, setText] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (taRef.current) taRef.current.focus();
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const top = anchorRect ? anchorRect.bottom + 6 : 80;
  const left = anchorRect ? Math.max(12, anchorRect.left - 8) : 24;

  const submit = () => {
    const v = text.trim();
    if (!v) return;
    onSubmit(v);
  };

  return (
    <div ref={ref} className="fm-prompt-popover slide-up" style={{ top, left }}>
      <div
        className="hstack"
        style={{ justifyContent: "space-between", marginBottom: 8 }}
      >
        <div className="hstack gap-2">
          <Icon name="sparkle" size={12} />
          <span className="label" style={{ margin: 0 }}>
            New formulation from prompt
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--ink-3)",
            cursor: "pointer",
            padding: 2,
            lineHeight: 0,
          }}
        >
          <Icon name="x" size={11} />
        </button>
      </div>

      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="e.g. Push tropical & estery up; trim caramel base by ~30%; keep cost under $0.40/100g"
        rows={4}
        className="fm-prompt-textarea"
      />

      <div
        className="hstack"
        style={{ justifyContent: "space-between", marginTop: 10 }}
      >
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          ⌘↵ to generate
        </span>
        <div className="hstack gap-2">
          <button type="button" className="btn xs ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn xs"
            onClick={submit}
            disabled={!text.trim()}
            style={{ opacity: text.trim() ? 1 : 0.5 }}
          >
            <Icon name="sparkle" size={11} />
            Generate
          </button>
        </div>
      </div>
    </div>
  );
}
